import { formatMessage, FormattedHTMLMessage } from '@99designs/common/i18n';
import { SquarespaceApiBrief } from '__generated__/SquarespaceApiBrief';
import { SquarespaceApiIndustry } from '__generated__/types';
import { handlePromise } from 'common/utils/Bugsnag';
import ContentSetting, { Sizes } from 'projects/components/ContentSetting';
import SquarespaceDesignerConfirmationModal from 'projects/components/SquarespaceDesignerConfirmationModal';
import SquarespaceDesignerResults from 'projects/components/SquarespaceDesignerResults';
import SquarespaceDesignerSelection from 'projects/components/SquarespaceDesignerSelection';
import SquarespaceFooter from 'projects/components/SquarespaceFooter';
import { Color, MAX_DESIGNERS } from 'projects/consts';
import { trackRemovedDesignerEvent } from 'projects/events';
import RemoveStructuredBriefDesigners from 'projects/mutations/RemoveStructuredBriefDesigners';
import { getIndustry } from 'projects/routes/squarespace/industry';
import * as React from 'react';

export interface Props {
    brief: SquarespaceApiBrief;
    launch(): Promise<void>;
}

export default function DesignersPage(props: Props) {
    const [isModalVisiable, showModal] = React.useState(false);
    const { brief } = props;
    const { industry } = props.brief.fields as { industry: SquarespaceApiIndustry };

    return (
        <>
            <ContentSetting size={Sizes.Full}>
                <div className="paragraph paragraph--centered paragraph--double-margin">
                    <FormattedHTMLMessage
                        message={__(
                            'Your designers have been carefully chosen based on the category and industry: <b>Squarespace / {industry}</b><br />Please select up to five designers to invite. You’ll receive custom quote(s) which you can approve to kick off your exciting website project!'
                        )}
                        values={{
                            industry: industry ? getIndustry(industry) : '-',
                        }}
                    />
                </div>
                <SquarespaceDesignerResults brief={brief} />
            </ContentSetting>
            <SquarespaceDesignerConfirmationModal
                brief={brief}
                onDismiss={() => showModal(false)}
                onConfirm={() => props.launch()}
                disabled={false}
                showing={isModalVisiable}
            />
            <SquarespaceFooter
                nextAction={() => showModal(true)}
                nextActionLabel={__('Next')}
                disabled={brief.designers.length == 0}
            >
                <RemoveStructuredBriefDesigners>
                    {removeDesigners => (
                        <SquarespaceDesignerSelection
                            showTooltips
                            designers={brief.designers}
                            onRemoveDesigner={designerId => {
                                trackRemovedDesignerEvent({
                                    id: brief.id,
                                    designer_id: designerId,
                                });
                                handlePromise(
                                    removeDesigners({
                                        id: brief.id,
                                        designerIds: [designerId],
                                    }),
                                    'SquarespaceDesignersPage RemoveDesigners'
                                );
                            }}
                            color={Color.squarespaceBlack}
                            selectDesignersCopy={{
                                heading: __('Selected designers'),
                                message: formatMessage(
                                    __(
                                        "Choose up to {maxDesigners} designers to invite. When ready, click 'Next' to review and request quotes."
                                    ),
                                    {
                                        maxDesigners: MAX_DESIGNERS,
                                    }
                                ),
                                storageKey:
                                    'squarespace_designer_selection_designer_placeholder_tooltip',
                            }}
                            confirmDesignersCopy={{
                                heading: __('You’re done!'),
                                message: __(
                                    'Simply confirm your chosen designers to request quote(s) and get underway.'
                                ),
                                storageKey: 'squarespace_designer_selection_max_designers_tooltip',
                            }}
                        />
                    )}
                </RemoveStructuredBriefDesigners>
            </SquarespaceFooter>
        </>
    );
}
